"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var _a;
Object.defineProperty(exports, "__esModule", { value: true });
exports.useDeleteClientRoleMutation = exports.useUpdateClientRoleMutation = exports.useCreateClientRoleMutation = exports.useGetClientRolesQuery = exports.useUpdateClientUserMutation = exports.useCreateClientUserMutation = exports.useAddClientIntegrationMutation = exports.useGetBranchesByIdsQuery = exports.useGetBranchesQuery = exports.useGetClientUserQuery = exports.useGetClientUsersQuery = exports.useGetClientSubscriptionQuery = exports.useGetClientQuery = exports.clientApi = void 0;
var react_1 = require("@reduxjs/toolkit/query/react");
var base_query_1 = require("./base_query");
// The client service is a separate deployment with its own base URL, so it needs its own api slice.
var clientApi = (0, react_1.createApi)({
    reducerPath: "clientApi",
    baseQuery: (0, base_query_1.authorisedBaseQuery)((_a = process.env.LETS_TRADE_CLIENT_URL) !== null && _a !== void 0 ? _a : ""),
    refetchOnMountOrArgChange: 30,
    refetchOnFocus: true,
    tagTypes: ["Branch", "Client", "ClientRole", "ClientSubscription", "ClientUser"],
    endpoints: function (builder) { return ({
        // the route identifies the client by id alone and rejects a bearer token
        getClient: builder.query({
            query: function (clientId) { return ({ url: "v2/client", params: { _id: clientId } }); },
            extraOptions: { token: base_query_1.noToken },
            transformResponse: function (response) { return (0, base_query_1.unwrapEnvelope)(response); },
            providesTags: ["Client"],
        }),
        getClientSubscription: builder.query({
            query: function () { return ({ url: "v2/client/stripe/subscription" }); },
            transformResponse: function (response) { var _a; return (_a = (0, base_query_1.unwrapEnvelope)(response)) !== null && _a !== void 0 ? _a : null; },
            providesTags: ["ClientSubscription"],
        }),
        addClientIntegration: builder.mutation({
            query: function (body) { return ({ url: "v2/add/client/integration", method: "PUT", body: body }); },
            invalidatesTags: ["Client"],
        }),
        // this service nests the page under content as user_list with its own count, rather than the pagination sibling used elsewhere
        // these routes resolve the caller through attachCurrentClientUser, which needs the user token: the client token carries no user id
        getClientUsers: builder.query({
            query: function (args) { return ({ url: "v2/client/user/list", params: args }); },
            extraOptions: { token: base_query_1.userToken },
            transformResponse: function (response) {
                var _a, _b;
                var content = (0, base_query_1.unwrapEnvelope)(response);
                return { items: (_a = content === null || content === void 0 ? void 0 : content.user_list) !== null && _a !== void 0 ? _a : [], count: (_b = content === null || content === void 0 ? void 0 : content.count) !== null && _b !== void 0 ? _b : 0 };
            },
            providesTags: ["ClientUser"],
        }),
        getClientUser: builder.query({
            query: function (userId) { return ({ url: "v2/client/user", params: { _id: userId } }); },
            extraOptions: { token: base_query_1.userToken },
            transformResponse: function (response) { return (0, base_query_1.unwrapEnvelope)(response); },
            providesTags: function (_result, _error, userId) { return [{ type: "ClientUser", id: userId }]; },
        }),
        // an admin sees every branch on the client; a manager only sees the ones they own, fetched by id list
        // the route requires client_id and takes no bearer, resolving a manager's own branches from the caller instead
        getBranches: builder.query({
            query: function (args) { return ({ url: "v2/client/branches", params: args }); },
            extraOptions: { token: base_query_1.noToken },
            transformResponse: function (response) {
                var _a, _b;
                var content = (0, base_query_1.unwrapEnvelope)(response);
                return { items: (_a = content === null || content === void 0 ? void 0 : content.branch_list) !== null && _a !== void 0 ? _a : [], count: (_b = content === null || content === void 0 ? void 0 : content.count) !== null && _b !== void 0 ? _b : 0 };
            },
            providesTags: ["Branch"],
        }),
        getBranchesByIds: builder.query({
            query: function (branchIds) { return ({ url: "v2/get/branches", method: "POST", body: { branch_id_list: branchIds } }); },
            transformResponse: function (response) { var _a, _b; return (_b = (_a = (0, base_query_1.unwrapEnvelope)(response)) === null || _a === void 0 ? void 0 : _a.branch_list) !== null && _b !== void 0 ? _b : []; },
            providesTags: ["Branch"],
        }),
        // the client record carries its own copy of the users array, so it goes stale alongside the list
        createClientUser: builder.mutation({
            query: function (body) { return ({ url: "v2/create/client/user", method: "POST", body: body }); },
            extraOptions: { token: base_query_1.userToken },
            transformResponse: function (response) { return (0, base_query_1.unwrapEnvelope)(response); },
            invalidatesTags: ["Client", "ClientUser"],
        }),
        updateClientUser: builder.mutation({
            query: function (body) { return ({ url: "v2/update/client/user", method: "PUT", body: body }); },
            extraOptions: { token: base_query_1.userToken },
            transformResponse: function (response) { return (0, base_query_1.unwrapEnvelope)(response); },
            invalidatesTags: ["Client", "ClientUser"],
        }),
        // the service seeds the built-in roles on this call, so a client always lists at least Admin and the two manager roles
        getClientRoles: builder.query({
            query: function () { return ({ url: "v2/client/role/list" }); },
            extraOptions: { token: base_query_1.userToken },
            transformResponse: function (response) { var _a, _b; return (_b = (_a = (0, base_query_1.unwrapEnvelope)(response)) === null || _a === void 0 ? void 0 : _a.role_list) !== null && _b !== void 0 ? _b : []; },
            providesTags: ["ClientRole"],
        }),
        createClientRole: builder.mutation({
            query: function (body) { return ({ url: "v2/client/role", method: "POST", body: body }); },
            extraOptions: { token: base_query_1.userToken },
            transformResponse: function (response) { var _a; return (_a = (0, base_query_1.unwrapEnvelope)(response)) === null || _a === void 0 ? void 0 : _a.role; },
            invalidatesTags: ["ClientRole"],
        }),
        // users inheriting the role get its new permissions, so their cached records are stale too
        updateClientRole: builder.mutation({
            query: function (_a) {
                var role_id = _a.role_id, body = __rest(_a, ["role_id"]);
                return ({ url: "v2/client/role/".concat(role_id), method: "PUT", body: body });
            },
            extraOptions: { token: base_query_1.userToken },
            transformResponse: function (response) { var _a; return (_a = (0, base_query_1.unwrapEnvelope)(response)) === null || _a === void 0 ? void 0 : _a.role; },
            invalidatesTags: ["ClientRole", "ClientUser", "Client"],
        }),
        deleteClientRole: builder.mutation({
            query: function (roleId) { return ({ url: "v2/client/role/".concat(roleId), method: "DELETE" }); },
            extraOptions: { token: base_query_1.userToken },
            invalidatesTags: ["ClientRole"],
        }),
    }); },
});
exports.clientApi = clientApi;
var useGetClientQuery = clientApi.useGetClientQuery, useGetClientSubscriptionQuery = clientApi.useGetClientSubscriptionQuery, useGetClientUsersQuery = clientApi.useGetClientUsersQuery, useGetClientUserQuery = clientApi.useGetClientUserQuery, useGetBranchesQuery = clientApi.useGetBranchesQuery, useGetBranchesByIdsQuery = clientApi.useGetBranchesByIdsQuery, useAddClientIntegrationMutation = clientApi.useAddClientIntegrationMutation, useCreateClientUserMutation = clientApi.useCreateClientUserMutation, useUpdateClientUserMutation = clientApi.useUpdateClientUserMutation, useGetClientRolesQuery = clientApi.useGetClientRolesQuery, useCreateClientRoleMutation = clientApi.useCreateClientRoleMutation, useUpdateClientRoleMutation = clientApi.useUpdateClientRoleMutation, useDeleteClientRoleMutation = clientApi.useDeleteClientRoleMutation;
exports.useGetClientQuery = useGetClientQuery;
exports.useGetClientSubscriptionQuery = useGetClientSubscriptionQuery;
exports.useGetClientUsersQuery = useGetClientUsersQuery;
exports.useGetClientUserQuery = useGetClientUserQuery;
exports.useGetBranchesQuery = useGetBranchesQuery;
exports.useGetBranchesByIdsQuery = useGetBranchesByIdsQuery;
exports.useAddClientIntegrationMutation = useAddClientIntegrationMutation;
exports.useCreateClientUserMutation = useCreateClientUserMutation;
exports.useUpdateClientUserMutation = useUpdateClientUserMutation;
exports.useGetClientRolesQuery = useGetClientRolesQuery;
exports.useCreateClientRoleMutation = useCreateClientRoleMutation;
exports.useUpdateClientRoleMutation = useUpdateClientRoleMutation;
exports.useDeleteClientRoleMutation = useDeleteClientRoleMutation;
